// =============================================================================
// staticSite/parityChecker — bramki bezpieczenstwa migracji.
//
//   G1 money links (HARD)  — kazdy <a> do hosta sprzedazowego w zrodlowym HTML
//                            musi byc w renderze BAJT W BAJT (href + rel + anchor)
//   G2 linki wychodzace    — liczba <a> zewnetrznych w renderze == w zrodle (HARD)
//   G3 parity URL (HARD)   — kazdy URL WP (strony + live sitemap) = plik albo redirect
//   G4 obrazy / H2 (WARN)  — render nie zgubil obrazow ani naglowkow z tresci
//   G5 meta (HARD)         — title / description / canonical / og:title na kazdym HTML
//   G6 schema (WARN)       — JSON-LD parsuje sie, brak bledow generatora
// Czyste funkcje na stringach — zero DOM, testowalne.
// =============================================================================

import type {
  GateResult, MissingUrl, OutboundLink, PageModel, PageParity, ParityReport, RenderedFile,
  SiteModel, StaticRedirectRow,
} from "./types";
import { filePathForUrlPath, hostOf, isSiteHost, normalizePath, pathFromUrl } from "./urlPolicy";
import { isPathRedirected } from "./redirectsBuilder";

const A_TAG_RE = /<a\b[^>]*>[\s\S]*?<\/a>/gi;
const HREF_RE = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
const REL_RE = /\brel\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

export interface ExtractedAnchor {
  raw: string;
  href: string;
  host: string;
  rel: string;
  anchor: string;
  external: boolean;
}

export function extractAnchors(html: string, domain: string): ExtractedAnchor[] {
  const out: ExtractedAnchor[] = [];
  for (const raw of html.match(A_TAG_RE) || []) {
    const open = raw.match(/<a\b[^>]*>/i)?.[0] || "";
    const href = (open.match(HREF_RE)?.[1] ?? open.match(HREF_RE)?.[2] ?? "").trim();
    const rel = (open.match(REL_RE)?.[1] ?? open.match(REL_RE)?.[2] ?? "").trim();
    const anchor = raw.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    const host = hostOf(href);
    const external = Boolean(host) && !isSiteHost(host, domain);
    out.push({ raw, href, host, rel, anchor, external });
  }
  return out;
}

export function isMoneyHost(host: string, moneyHosts: string[]): boolean {
  if (!host) return false;
  if (!moneyHosts.length) return true; // brak listy = kazdy zewnetrzny host jest money
  const h = host.toLowerCase().replace(/^www\./, "");
  return moneyHosts.some((m) => {
    const mm = m.toLowerCase().trim().replace(/^www\./, "");
    return mm && (h === mm || h.endsWith(`.${mm}`));
  });
}

export function outboundLinksOf(page: PageModel, site: SiteModel): OutboundLink[] {
  return extractAnchors(page.bodyHtml, site.domain)
    .filter((a) => a.external)
    .map((a) => ({
      href: a.href,
      host: a.host,
      anchor: a.anchor,
      rel: a.rel,
      is_money: isMoneyHost(a.host, site.settings.money_hosts),
    }));
}

export interface ParityInput {
  site: SiteModel;
  files: RenderedFile[];
  redirects: StaticRedirectRow[];
}

function fileText(f: RenderedFile): string {
  if (f.text != null) return f.text;
  return new TextDecoder().decode(f.bytes);
}

function count(re: RegExp, s: string): number {
  return (s.match(re) || []).length;
}

/** Propozycja przekierowania dla brakujacego URL-a. */
export function suggestRedirect(path: string, site: SiteModel): { to: string | null; reason: string } {
  const base = (site.settings.category_base || "category").replace(/^\/+|\/+$/g, "");
  if (/^\/page\/\d+\/$/.test(path)) return { to: "/", reason: "paginacja home" };
  const catPage = path.match(new RegExp(`^/${base}/([^/]+)/page/\\d+/$`));
  if (catPage) return { to: `/${base}/${catPage[1]}/`, reason: "paginacja kategorii" };
  if (path.startsWith("/tag/")) return { to: "/", reason: "archiwum tagu" };
  if (path.startsWith("/author/")) return { to: site.author?.path || "/", reason: "archiwum autora" };
  if (/^\/\d{4}\/(\d{2}\/)?(\d{2}\/)?$/.test(path)) return { to: "/", reason: "archiwum daty" };
  if (path.startsWith(`/${base}/`)) {
    const slug = path.slice(base.length + 2).replace(/\/$/, "");
    const cat = site.categories.find((c) => c.slug === slug);
    if (cat) return { to: cat.path, reason: "kategoria" };
    return { to: "/", reason: "nieznana kategoria" };
  }
  // Ten sam ostatni segment sciezki -> prawdopodobnie ta sama strona pod inna sciezka
  const last = path.replace(/\/$/, "").split("/").pop() || "";
  if (last) {
    const hit = site.pages.find((pg) => pg.path.replace(/\/$/, "").split("/").pop() === last);
    if (hit) return { to: hit.path, reason: "ten sam slug pod inna sciezka" };
  }
  return { to: null, reason: "brak odpowiednika" };
}

export function runParity(input: ParityInput): ParityReport {
  const { site, files, redirects } = input;
  const filesByPath = new Map(files.map((f) => [f.path, f]));
  const htmlFiles = files.filter((f) => f.path.endsWith(".html"));

  const g1: GateResult = { id: "G1", label: "Linki sprzedażowe nietknięte", level: "hard", passed: true, issues: [] };
  const g2: GateResult = { id: "G2", label: "Liczba linków wychodzących", level: "hard", passed: true, issues: [] };
  const g3: GateResult = { id: "G3", label: "Każdy adres WP ma odpowiednik", level: "hard", passed: true, issues: [] };
  const g4: GateResult = { id: "G4", label: "Obrazy i nagłówki z treści", level: "warn", passed: true, issues: [] };
  const g5: GateResult = { id: "G5", label: "Meta title / description / canonical", level: "hard", passed: true, issues: [] };
  const g6: GateResult = { id: "G6", label: "Schema.org JSON-LD", level: "warn", passed: true, issues: [] };

  const pages: PageParity[] = [];
  let moneyLinksTotal = 0;

  for (const page of site.pages) {
    if (page.kind === "not_found") continue;
    const file = filesByPath.get(filePathForUrlPath(page.path));
    const issues: string[] = [];
    const sourceAnchors = extractAnchors(page.bodyHtml, site.domain);
    const outbound = sourceAnchors.filter((a) => a.external);
    const money = outbound.filter((a) => isMoneyHost(a.host, site.settings.money_hosts));
    moneyLinksTotal += money.length;

    const row: PageParity = {
      path: page.path,
      kind: page.kind,
      title: page.title,
      sourceUrl: page.sourceUrl,
      moneyLinks: money.length,
      outboundLinks: outbound.length,
      g1: null, g2: null, g4: null, g5: null, g6: null,
      issues,
    };

    if (!file) {
      issues.push("Brak wyrenderowanego pliku");
      g3.passed = false;
      g3.issues.push(`${page.path}: brak pliku`);
      row.g1 = money.length ? false : null;
      row.g5 = false;
      pages.push(row);
      continue;
    }
    const html = fileText(file);

    // G1 — money links bajt w bajt
    if (page.kind !== "home" && page.kind !== "category") {
      let ok = true;
      for (const a of money) {
        if (!html.includes(a.raw)) {
          ok = false;
          issues.push(`Money link zmieniony/zgubiony: ${a.href}`);
          g1.issues.push(`${page.path}: ${a.href}`);
        }
      }
      row.g1 = money.length ? ok : null;
      if (!ok) g1.passed = false;

      // G2 — liczba zewnetrznych
      const renderedOutbound = extractAnchors(html, site.domain).filter((a) => a.external).length;
      row.g2 = renderedOutbound === outbound.length;
      if (!row.g2) {
        g2.passed = false;
        const msg = `${page.path}: zrodlo ${outbound.length}, render ${renderedOutbound}`;
        g2.issues.push(msg);
        issues.push(`Linki wychodzące: ${msg}`);
      }

      // G4 — obrazy / H2 (render moze miec WIECEJ: featured, karty, foto autora)
      const srcImgs = count(/<img\b/gi, page.bodyHtml);
      const srcH2 = count(/<h2\b/gi, page.bodyHtml);
      const outImgs = count(/<img\b/gi, html);
      const outH2 = count(/<h2\b/gi, html);
      row.g4 = outImgs >= srcImgs && outH2 >= srcH2;
      if (!row.g4) {
        g4.passed = false;
        g4.issues.push(`${page.path}: img ${srcImgs}->${outImgs}, h2 ${srcH2}->${outH2}`);
        issues.push("Render zgubił obrazy lub nagłówki");
      }
    }

    // G5 — meta
    const hasTitle = /<title>[^<]+<\/title>/i.test(html);
    const hasDesc = /<meta\s+name="description"\s+content="[^"]+"/i.test(html);
    const hasCanonical = /<link\s+rel="canonical"\s+href="https?:\/\/[^"]+"/i.test(html);
    const hasOg = /<meta\s+property="og:title"/i.test(html);
    row.g5 = hasTitle && hasCanonical && hasOg && (hasDesc || page.kind === "home");
    if (!row.g5) {
      g5.passed = false;
      const missing = [!hasTitle && "title", !hasDesc && "description", !hasCanonical && "canonical", !hasOg && "og:title"].filter(Boolean).join(", ");
      g5.issues.push(`${page.path}: brak ${missing}`);
      issues.push(`Meta: brak ${missing}`);
    }

    // G6 — schema
    if (page.kind === "article" || page.kind === "pillar" || page.kind === "author") {
      let ok = page.schemaErrors.length === 0;
      const ld = html.match(/<script type="application\/ld\+json">([\s\S]*?)<\/script>/i);
      if (ld) {
        try { JSON.parse(ld[1].replace(/<\\\/script/g, "</script")); } catch { ok = false; issues.push("JSON-LD nie parsuje się"); }
      } else if (page.schemaGraph) {
        ok = false;
        issues.push("JSON-LD nie trafił do <head>");
      }
      for (const e of page.schemaErrors) issues.push(`Schema: ${e}`);
      row.g6 = ok;
      if (!ok) { g6.passed = false; g6.issues.push(`${page.path}: ${issues.filter((i) => /schema|json-ld/i.test(i)).join("; ")}`); }
    }

    pages.push(row);
  }

  // G3 — inwentarz URL-i WP
  const missingUrls: MissingUrl[] = [];
  const inventory = new Set<string>();
  for (const pg of site.pages) if (pg.sourceUrl) inventory.add(pg.sourceUrl);
  for (const u of site.wpSitemapUrls) inventory.add(u);
  for (const url of inventory) {
    const host = hostOf(url);
    if (host && !isSiteHost(host, site.domain)) continue;
    const path = pathFromUrl(url);
    if (path === "/") continue;
    if (/\.(xml|jpe?g|png|gif|webp|svg|pdf|css|js)$/i.test(path)) continue;
    const file = filesByPath.get(filePathForUrlPath(path));
    if (file) continue;
    if (isPathRedirected(path, redirects)) continue;
    const sug = suggestRedirect(normalizePath(path), site);
    missingUrls.push({ url, path, suggestedRedirect: sug.to, reason: sug.reason });
  }
  if (missingUrls.length) {
    g3.passed = false;
    for (const m of missingUrls.slice(0, 50)) g3.issues.push(`${m.path} (${m.reason})`);
    if (missingUrls.length > 50) g3.issues.push(`… i ${missingUrls.length - 50} więcej`);
  }

  // G5 — 404 i pliki bez PageModel (kategorie)
  for (const f of htmlFiles) {
    if (f.path === "/404.html") continue;
    const covered = site.pages.some((pg) => filePathForUrlPath(pg.path) === f.path);
    if (covered) continue;
    const html = fileText(f);
    if (!/<title>[^<]+<\/title>/i.test(html) || !/<link\s+rel="canonical"/i.test(html)) {
      g5.passed = false;
      g5.issues.push(`${f.path}: brak title/canonical`);
    }
  }

  const gates = [g1, g2, g3, g5, g4, g6];
  const blocking = gates.some((g) => g.level === "hard" && !g.passed);
  return {
    gates,
    pages,
    missingUrls,
    blocking,
    summary: {
      blocking,
      pages: pages.length,
      hardFailures: gates.filter((g) => g.level === "hard" && !g.passed).length,
      warnings: gates.filter((g) => g.level === "warn" && !g.passed).length,
      moneyLinksTotal,
      missingUrls: missingUrls.length,
      analyzedAt: new Date().toISOString(),
    },
  };
}
